import { Box, Divider, useTheme } from "@mui/material";
import { Outlet, useMatches } from "react-router-dom";

import { useNavigation } from "@/providers";
import {
  PageLayout,
  RobotoStyledTab,
  RobotoStyledTabs,
} from "@/shared/components";
import { type ActionsTabName } from "@/shared/navigation/types";

/**
 * Route `handle` each tab route attaches so the tab bar can identify the
 * active tab from the matched route rather than by re-parsing the URL. See
 * `ActionsRoutes`.
 */
export interface ActionsTabHandle {
  actionsTab: ActionsTabName;
}

function actionsTabFromHandle(handle: unknown): ActionsTabName | undefined {
  return handle !== null && typeof handle === "object" && "actionsTab" in handle
    ? (handle as ActionsTabHandle).actionsTab
    : undefined;
}

/**
 * Tabs of the Actions page, in display order. Each tab has its own route, so
 * every tab is deep-linkable. Triggers and Invocations live at the top level
 * (`/triggers`, `/invocations`) to share a prefix with their detail pages
 * (`/triggers/:name`, `/invocations/:id`); the rest sit under `/actions`.
 *
 * `tab` is the name understood by `Goto.actions` (navigation) and matched
 * against each route's `actionsTab` handle (active-tab detection).
 */
const ACTIONS_TABS = [
  { label: "Actions", tab: "actions" },
  { label: "Hub", tab: "hub" },
  { label: "Triggers", tab: "triggers" },
  { label: "Invocations", tab: "invocations" },
  { label: "Images", tab: "images" },
] as const satisfies ReadonlyArray<{ label: string; tab: ActionsTabName }>;

/**
 * Layout for the Actions page: renders the page chrome and tab bar, with the
 * active tab's content supplied through the router `<Outlet />`.
 */
export const ActionsTabs: React.FC = () => {
  const theme = useTheme();
  const { goto } = useNavigation();

  // Read the active tab from the matched route's handle, so the router remains
  // the single source of truth for which tab a URL maps to. Only one matched
  // route carries an `actionsTab` handle; fall back to the index tab
  // defensively.
  const matches = useMatches();
  const activeTab: ActionsTabName =
    matches
      .map((match) => actionsTabFromHandle(match.handle))
      .find((tab): tab is ActionsTabName => tab !== undefined) ?? "actions";

  return (
    <PageLayout title={"Roboto - Actions"} headerContentLeft={<>Actions</>}>
      <div>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <RobotoStyledTabs
            value={activeTab}
            onChange={(_event, newTab: ActionsTabName) => {
              goto.actions({ tab: newTab });
            }}
            aria-label="actions tabs"
          >
            {ACTIONS_TABS.map((tab) => (
              <RobotoStyledTab
                key={tab.tab}
                value={tab.tab}
                label={tab.label}
              />
            ))}
          </RobotoStyledTabs>
        </Box>
        <Divider
          sx={{
            borderColor: theme.border.color,
          }}
        />
      </div>

      <Box
        sx={{
          padding: theme.spacing(2, 0),
          paddingBottom: theme.spacing(1),
        }}
      >
        <Outlet />
      </Box>
    </PageLayout>
  );
};
